import React, { useEffect, useState } from 'react';
import BottomSheet from '../../../components/ui/BottomSheet';
import ConfirmationDialog from '../../../components/ConfirmationDialog';
import PlannedPill from './PlannedPill';
import KebabMenu, { KebabIcons } from './KebabMenu';
import Switch from '../../../components/ui/Switch';
import Tooltip from '../../../components/ui/Tooltip';
import NightsStepper from '../../../components/NightsStepper';
import { Dots, NightsDots, useMinLoading } from './RouteTimeline';
import { fetchStopBookingLink, StopStayDetails } from '../../journeys/api';
import { getCountryFlag } from '../../../shared/countryFlag';
import { formatEuroExact, parseEuroInput } from '../utils';

// Stop detail sheet (prototype #stopSheet, handover §6), ACCOMMODATION-first
// slice (backlog #10 / #4 phase 2): hero, blurb, and the "Where you'll stay"
// section — a Booking.com search CTA for unplanned stays (plain link until
// the affiliate registration lands, docs/integrations/booking.md) plus
// user-entered stay details (place, address, booked via, price), the
// ticketDetails twin on the stop side. Same draft pattern as LegSheet:
// edits stage locally and commit on Save; the planned pill rides the same
// save. Deliberately NOT here in v1: PDF attach (the app has no file
// storage), `things_to_do` (returns with a later stop-sheet slice), and the
// prototype's "Show in map" (needs geocoded stay addresses).

export interface StopSheetStop {
  id: string;
  city?: string | null;
  country?: string | null;
  description?: string | null;
  accommodationSuggestion?: string | null;
  activities?: string[];
  things_to_do?: string[];
  stopDurationHours?: number | null;
  planned?: boolean;
  stayDetails?: StopStayDetails | null;
}

export interface StopSheetProps {
  journeyId: string | null;
  /** The stop being viewed (live from journey state), or null when closed. */
  stop: StopSheetStop | null;
  open: boolean;
  busy?: boolean;
  /** Expected stay window (ISO days) from the schedule walk; rides into the
   *  Booking.com link so the search lands prefilled. */
  checkin?: string | null;
  checkout?: string | null;
  /** The window as the timeline shows it ("16–19 Aug"), under the stepper. */
  dateLabel?: string | null;
  /** Live nights edits from the sheet's stepper (the stepper persists itself
   *  on a debounce, same component as the timeline's; this lets the page keep
   *  journey state — and therefore the timeline — in sync). */
  onNightsChange?: (stopId: string, nights: number) => void;
  /** A nights change was saved (the stepper saves itself, after a pause). */
  onNightsSaved?: (stopId: string) => void;
  /** Nights suggestion still running: the stepper shows the dots loader. */
  nightsPending?: boolean;
  /** Where the "Find stays" button gets its link when the sheet has no
   *  journeyId, i.e. on the read-only shared view (its public, share-token
   *  endpoint). With a journeyId the sheet uses the journey's own route. */
  bookingLink?: (stopId: string, ctx: { checkin: string | null; checkout: string | null; source: 'stop_sheet' }) => Promise<{ url: string }>;
  /** Persist the drafted changes. Only changed fields are present. Resolve true on success. */
  onSave: (stopId: string, changes: { planned?: boolean; stayDetails?: StopStayDetails | null }) => Promise<boolean>;
  /** Kebab → Replace: the page closes the sheet and puts the timeline row into
   *  its city-picker mode (the picker lives on the row, not here). */
  onReplace?: (stopId: string) => void;
  /** Kebab → Delete (the page confirms). */
  onDelete?: (stopId: string) => void;
  onClose: () => void;
}

// Draft shape: text fields as the user types them; cost as a display string
// ("120" / "120.50") parsed on commit like the leg sheet's price inputs.
interface StayDraft {
  placeName: string;
  address: string;
  bookedVia: string;
  cost: string;
}

const draftFrom = (d?: StopStayDetails | null): StayDraft => ({
  placeName: d?.placeName || '',
  address: d?.address || '',
  bookedVia: d?.bookedVia || '',
  cost: d?.costCents != null ? (d.costCents % 100 === 0 ? String(d.costCents / 100) : (d.costCents / 100).toFixed(2)) : '',
});

// Canonical string for the dirty check (trimmed values only).
const stayKey = (d: StayDraft): string =>
  JSON.stringify({ p: d.placeName.trim(), a: d.address.trim(), b: d.bookedVia.trim(), c: d.cost.trim() });

/** Draft → API object; null when every field is empty (clears the details). */
function stayFromDraft(d: StayDraft): StopStayDetails | null {
  const out: StopStayDetails = {};
  if (d.placeName.trim()) out.placeName = d.placeName.trim();
  if (d.address.trim()) out.address = d.address.trim();
  if (d.bookedVia.trim()) out.bookedVia = d.bookedVia.trim();
  const cents = parseEuroInput(d.cost);
  if (cents != null) out.costCents = cents;
  return Object.keys(out).length ? out : null;
}

const sheetH: React.CSSProperties = { fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: 15, color: '#16241d', margin: 0 };
const fieldRow: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '11px 14px', borderBottom: '1px solid rgba(20,40,30,0.06)' };
const rowLab: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#6c7872', flex: 'none' };
const inputStyle: React.CSSProperties = {
  font: 'inherit', fontSize: 14, fontWeight: 600, color: '#16241d', textAlign: 'right',
  border: '1px solid rgba(20,40,30,0.14)', borderRadius: 9, padding: '6px 9px', width: '100%', maxWidth: 240,
};
const ctaSecondary: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, flex: 'none', font: 'inherit', fontSize: 11.5,
  fontWeight: 700, borderRadius: 12, padding: '8px 12px', cursor: 'pointer',
  background: '#f4f6f3', color: '#6c7872', border: '1px solid rgba(20,40,30,0.06)',
};

const ExternalGlyph: React.FC = () => (
  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.2" aria-hidden>
    <path d="M7 17L17 7M9 7h8v8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const StopSheet: React.FC<StopSheetProps> = ({ journeyId, stop, open, busy, checkin, checkout, dateLabel, onNightsChange, onNightsSaved, nightsPending, bookingLink, onSave, onReplace, onDelete, onClose }) => {
  const [draft, setDraft] = useState<StayDraft>(draftFrom(null));
  const [planned, setPlanned] = useState(false);
  const [editingField, setEditingField] = useState<keyof StayDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [askDiscard, setAskDiscard] = useState(false);
  const [bookingBusy, setBookingBusy] = useState(false);
  const [bookingError, setBookingError] = useState(false);
  const showNightsLoader = useMinLoading(Boolean(nightsPending));
  // Place name / address / booked via start collapsed behind a disclosure so
  // the section stays short and "Things to do" sits higher up the sheet
  // (Paulo 2026-08-24). Auto-expanded when the user already filled any of
  // them, so a disclosure never hides their own content.
  const [placeDetailsOpen, setPlaceDetailsOpen] = useState(false);

  // Re-arm the draft whenever a (different) stop opens.
  useEffect(() => {
    if (!open || !stop) return;
    setDraft(draftFrom(stop.stayDetails));
    setPlanned(Boolean(stop.planned));
    setEditingField(null);
    setBookingError(false);
    const d = stop.stayDetails;
    setPlaceDetailsOpen(Boolean(d && (d.placeName || d.address || d.bookedVia)));
  }, [open, stop?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!stop) return null;

  const stayDirty = stayKey(draft) !== stayKey(draftFrom(stop.stayDetails));
  const dirty = stayDirty || planned !== Boolean(stop.planned);
  // Price must parse as a number before anything can be saved (validation,
  // Paulo 2026-08-18): non-empty text that parseEuroInput rejects blocks the
  // Save button and shows the inline error under the rows.
  const costInvalid = draft.cost.trim() !== '' && parseEuroInput(draft.cost) == null;
  const flag = getCountryFlag(stop.country);
  const saved = stop.stayDetails || null;
  const hasAnyStay = Boolean(saved && (saved.placeName || saved.address || saved.bookedVia || saved.costCents != null));
  const activities = stop.activities || stop.things_to_do || [];

  // Country only — the nights count already lives in the stepper column
  // (a "2 nights" here too was redundant, Paulo 2026-08-18).
  const summaryBits: string[] = [];
  if (stop.country) summaryBits.push(String(stop.country));

  const requestClose = () => {
    if (saving) return;
    if (dirty) setAskDiscard(true);
    else onClose();
  };

  const save = async () => {
    if (!dirty || saving || costInvalid) return;
    setSaving(true);
    try {
      const changes: { planned?: boolean; stayDetails?: StopStayDetails | null } = {};
      if (stayDirty) changes.stayDetails = stayFromDraft(draft);
      if (planned !== Boolean(stop.planned)) changes.planned = planned;
      const ok = await onSave(stop.id, changes);
      if (ok) onClose();
    } finally {
      setSaving(false);
    }
  };

  // Blank window opened synchronously so popup blockers allow it (same
  // pattern as the Omio CTAs).
  const openBooking = () => {
    if (!(journeyId || bookingLink) || bookingBusy) return;
    const win = window.open('', '_blank');
    setBookingBusy(true);
    setBookingError(false);
    const ctx = { checkin: checkin || null, checkout: checkout || null, source: 'stop_sheet' as const };
    (journeyId ? fetchStopBookingLink(journeyId, stop.id, ctx) : bookingLink!(stop.id, ctx))
      .then(({ url }) => {
        if (win) win.location.href = url;
        else window.open(url, '_blank');
      })
      .catch(() => {
        if (win) win.close();
        setBookingError(true);
      })
      .finally(() => setBookingBusy(false));
  };

  // One tap-to-edit row: shows the value (or an "Add …" hint) until tapped,
  // then an autofocused CONTROLLED input — every keystroke lands in the draft
  // so the Save bar appears while typing, not only on blur (Paulo
  // 2026-08-18). An invalid price keeps its raw text visible in red with the
  // inline error below the rows; Save stays blocked until it parses.
  const row = (label: string, field: keyof StayDraft, placeholder: string, opts?: { euro?: boolean }) => {
    const value = draft[field].trim();
    const invalid = opts?.euro && costInvalid;
    const display = value ? (opts?.euro && parseEuroInput(value) != null ? formatEuroExact(parseEuroInput(value)!) : value) : '';
    return (
      <div style={fieldRow}>
        <span style={rowLab}>{label}</span>
        {editingField === field ? (
          <input
            autoFocus
            value={draft[field]}
            placeholder={placeholder}
            aria-label={label.toLowerCase()}
            inputMode={opts?.euro ? 'decimal' : undefined}
            style={{ ...inputStyle, ...(invalid ? { borderColor: '#cf5a3c' } : {}) }}
            onChange={(e) => setDraft((d) => ({ ...d, [field]: e.target.value }))}
            onBlur={() => setEditingField(null)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
              if (e.key === 'Escape') setEditingField(null);
            }}
          />
        ) : (
          <button
            type="button"
            onClick={() => !saving && setEditingField(field)}
            style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer', font: 'inherit', fontSize: 14, fontWeight: 700, color: invalid ? '#cf5a3c' : display ? '#16241d' : '#aab4af', textAlign: 'right', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {display || placeholder}
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <BottomSheet
        open={open}
        onClose={requestClose}
        ariaLabel={`Stay details: ${stop.city || 'stop'}`}
        actions={dirty ? (
          <>
            <button
              type="button"
              onClick={requestClose}
              disabled={saving}
              style={{ flex: 'none', background: '#f4f6f3', color: '#6c7872', border: '1px solid rgba(20,40,30,0.06)', borderRadius: 12, padding: '12px 18px', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
            >
              Discard
            </button>
            <button
              type="button"
              onClick={save}
              disabled={saving || busy || costInvalid}
              style={{ flex: 1, background: '#157a55', color: '#fff', border: 'none', borderRadius: 12, padding: 12, fontSize: 14, fontWeight: 700, cursor: saving || costInvalid ? 'default' : 'pointer', opacity: saving || costInvalid ? 0.6 : 1 }}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </>
        ) : undefined}
      >
        {/* Hero. The nights stepper + expected window moved from top-right
            into the "Where you'll stay" card's Dates row (Paulo 2026-08-24:
            the stacked column crowded the corner). */}
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, padding: '6px 36px 2px 2px' }}>
          <span aria-hidden style={{ fontSize: 34, lineHeight: 1, flex: 'none' }}>{flag || '📍'}</span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
              <h2 style={{ fontFamily: 'Fraunces, Georgia, serif', fontWeight: 600, fontSize: 25, margin: 0, color: '#16241d', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{stop.city || 'Stop'}</h2>
              {(onReplace || onDelete) && (
                <KebabMenu
                  ariaLabel={`Options for ${stop.city || 'this stop'}`}
                  disabled={busy || saving}
                  items={[
                    { label: 'Replace', icon: KebabIcons.swap, hidden: !onReplace, onSelect: () => onReplace?.(stop.id) },
                    { label: 'Delete', icon: KebabIcons.trash, tone: 'danger', hidden: !onDelete, onSelect: () => onDelete?.(stop.id) },
                  ]}
                />
              )}
            </span>
            {summaryBits.length > 0 && <div style={{ fontSize: 13, color: '#6c7872', marginTop: 2 }}>{summaryBits.join(' · ')}</div>}
          </div>
        </div>

        {stop.description && (
          <div style={{ fontSize: 13.5, lineHeight: 1.5, color: '#6c7872', marginTop: 12 }}>{stop.description}</div>
        )}

        {/* Where you'll stay */}
        <div style={{ marginTop: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 11 }}>
            {/* The entered price rides the header so the total is visible
                without opening the row (stay-price visibility pass,
                Paulo 2026-08-18). User value: exact, no star. */}
            <h3 style={sheetH}>
              Where you'll stay
              {saved?.costCents != null && <span style={{ fontWeight: 600, color: '#6c7872' }}> · {formatEuroExact(Number(saved.costCents))}</span>}
            </h3>
            {/* Status display only (Paulo 2026-08-24): the "Stay booked"
                Switch row below is the control; this section is always
                visible, so the pill isn't pressable here. */}
            <Tooltip content={planned ? 'Booked: your stay here is sorted.' : 'Not booked yet. Use the toggle below once your stay is sorted.'} widthClassName="w-max">
              <span style={{ display: 'inline-flex' }}>
                <PlannedPill planned={planned} disabled={saving} />
              </span>
            </Tooltip>
          </div>
          {/* CTA on its own line under the header (Paulo 2026-08-24): title +
              pill + button on one line didn't fit, mobile or desktop. */}
          {!planned && (journeyId || bookingLink) && (
            <div style={{ marginBottom: 11 }}>
              <button
                type="button"
                data-share-ok
                onClick={openBooking}
                disabled={bookingBusy}
                style={{ ...ctaSecondary, width: '100%', justifyContent: 'center', cursor: bookingBusy ? 'default' : 'pointer', opacity: bookingBusy ? 0.7 : 1 }}
              >
                {bookingBusy ? <>Opening <Dots /></> : <>Find stays on Booking.com<ExternalGlyph /></>}
              </button>
            </div>
          )}
          {bookingError && (
            <div style={{ fontSize: 12, color: '#b3564a', marginBottom: 8 }}>
              Could not open the stay search. Please try again.
            </div>
          )}
          {/* Jody's accommodation hint from generation, until the user has
              entered their own stay. */}
          {!hasAnyStay && stop.accommodationSuggestion && (
            <div style={{ fontSize: 12.5, color: '#6c7872', marginBottom: 10 }}>
              💡 Jody's suggestion: {stop.accommodationSuggestion}
            </div>
          )}
          <div style={{ border: '1px solid rgba(20,40,30,0.06)', borderRadius: 16, overflow: 'hidden' }}>
            {/* The booked status control (Paulo 2026-08-24): a labeled Switch
                row, first in the section; the header pill only displays it.
                Draft state like the stay fields; commits on Save. */}
            <div style={fieldRow}>
              <span style={rowLab}>Stay booked</span>
              <Switch on={planned} onToggle={() => setPlanned(!planned)} ariaLabel="Stay booked" disabled={saving} />
            </div>
            {/* Dates + nights stepper in one row (Paulo 2026-08-24; moved
                here from the hero's top-right column). The stepper is the
                same self-persisting component the card uses; ticks patch
                journey state via onNightsChange so every surface follows. */}
            <div style={fieldRow}>
              <span style={rowLab}>Dates</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                {dateLabel && <span style={{ fontSize: 11.5, fontWeight: 700, color: '#6c7872', background: '#f4f6f3', border: '1px solid rgba(20,40,30,0.06)', borderRadius: 999, padding: '3px 9px', whiteSpace: 'nowrap' }}>{dateLabel}</span>}
                {showNightsLoader
                  ? <NightsDots />
                  : <NightsStepper stopId={stop.id} initialHours={stop.stopDurationHours ?? null} onNightsChange={(n) => onNightsChange?.(stop.id, n)} onSaved={() => onNightsSaved?.(stop.id)} />}
              </span>
            </div>
            {row('Total price', 'cost', 'e.g. 120', { euro: true })}
            {placeDetailsOpen && (
              <>
                {row('Place name', 'placeName', 'Add the place you booked')}
                {row('Address', 'address', 'Add the address')}
                {row('Booked via', 'bookedVia', 'e.g. Booking.com')}
              </>
            )}
          </div>
          {costInvalid && (
            <div style={{ fontSize: 12, color: '#cf5a3c', fontWeight: 600, marginTop: 6 }}>
              Enter the price as a number, like 120 or 120.50.
            </div>
          )}
          {/* Disclosure in the app's established style (the trip form's
              "Add details for a better plan"): label + rotating chevron. */}
          <button
            type="button"
            onClick={() => setPlaceDetailsOpen((v) => !v)}
            aria-expanded={placeDetailsOpen}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 8,
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
              font: 'inherit', fontSize: 13, fontWeight: 700, color: '#157a55',
            }}
          >
            {placeDetailsOpen ? 'Hide place and booking details' : 'Add place and booking details'}
            <svg
              width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden
              style={{ transform: placeDetailsOpen ? 'rotate(180deg)' : 'none', transition: 'transform .18s' }}
            >
              <path d="M3 6l5 5 5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <div style={{ fontSize: 10.5, color: '#aab4af', marginTop: 6 }}>tap a row to enter your stay's details</div>
        </div>

        {/* Things to do — moved here from the timeline's stop card (Paulo
            2026-08-18): the card stays dense, the sheet carries the content.
            Same chip styling the card used. */}
        {activities.length > 0 && (
          <div style={{ marginTop: 20 }}>
            <h3 style={sheetH}>Things to do</h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, marginTop: 10 }}>
              {activities.map((a, i) => (
                <span key={i} style={{ fontSize: 12, background: '#f4f6f3', border: '1px solid rgba(20,40,30,0.08)', borderRadius: 999, padding: '6px 11px', color: '#16241d' }}>{a}</span>
              ))}
            </div>
          </div>
        )}
      </BottomSheet>
      {/* The dialog carries a Save rescue path (Paulo 2026-08-18): saving
          straight from it instead of cancel-then-save. Hidden while the
          price is invalid — there is nothing valid to save yet. */}
      {askDiscard && (
        <ConfirmationDialog
          title="Discard changes?"
          message="Your unsaved stay details will be lost."
          confirmText="Discard"
          confirmButtonStyle="danger"
          onConfirm={() => { setAskDiscard(false); onClose(); }}
          onCancel={() => setAskDiscard(false)}
          extraText={costInvalid ? undefined : 'Save'}
          onExtra={costInvalid ? undefined : () => { setAskDiscard(false); void save(); }}
        />
      )}
    </>
  );
};

export default StopSheet;
